import {
  BookOpen,
  Type,
  Users,
  Mic,
  ShieldCheck,
  Package,
  Megaphone,
  FileCheck,
  Radio,
  CalendarDays,
  Image,
  Brain,
  Globe,
  Target,
  Radar,
  Activity,
} from 'lucide-react';

import { APP_ROUTES } from '@/app/routePaths';
import * as constants from '@/constants';
import * as dbConstants from '@dao/dbConstants';

import type { MenuGroup, MenuItem } from './types';

export const topItems: MenuItem[] = [
  { id: 'visibility-report', labelKey: 'sidebar.item.visibilityReport', icon: Radar, path: APP_ROUTES.RESCAN, relatedPaths: [APP_ROUTES.RESCAN], permission: dbConstants.PERMISSION_DASHBOARD_READ },
  { id: 'site-health', labelKey: 'sidebar.item.siteHealth', icon: Activity, path: APP_ROUTES.SITE_HEALTH, relatedPaths: [APP_ROUTES.SITE_HEALTH], permission: dbConstants.PERMISSION_DASHBOARD_READ },
];

export const menuGroups: MenuGroup[] = [
  {
    id: 'prepare',
    labelKey: 'sidebar.group.prepare',
    collapsible: true,
    items: [],
    subgroups: [
      {
        id: 'brand-brain',
        labelKey: 'sidebar.subgroup.brandBrain',
        icon: Brain,
        items: [
          { id: 'product', labelKey: 'sidebar.item.product', icon: Package, path: APP_ROUTES.PRODUCTS, relatedPaths: [APP_ROUTES.PRODUCTS, APP_ROUTES.PRODUCT_NEW], permission: dbConstants.PERMISSION_PRODUCT_READ },
          { id: 'knowledge-base', labelKey: 'sidebar.item.knowledgeBase', icon: BookOpen, path: APP_ROUTES.COMPANY_KNOWLEDGE_BASE, relatedPaths: [APP_ROUTES.COMPANY_KNOWLEDGE_BASE], permission: dbConstants.PERMISSION_KNOWLEDGE_BASE_READ },
          { id: 'competitors', labelKey: 'sidebar.item.competitors', icon: Target, path: APP_ROUTES.COMPETITORS, relatedPaths: [APP_ROUTES.COMPETITORS], permission: dbConstants.PERMISSION_KNOWLEDGE_BASE_READ },
          { id: 'brand-voice', labelKey: 'sidebar.item.brandVoice', icon: Mic, path: APP_ROUTES.COMPANY_BRAND_VOICE, relatedPaths: [APP_ROUTES.COMPANY_BRAND_VOICE], permission: dbConstants.PERMISSION_BRAND_VOICE_READ },
          { id: 'trust-signal', labelKey: 'sidebar.item.trustSignal', icon: ShieldCheck, path: APP_ROUTES.COMPANY_TRUST_SIGNAL, relatedPaths: [APP_ROUTES.COMPANY_TRUST_SIGNAL], permission: dbConstants.PERMISSION_ASSET_READ },
          { id: 'media', labelKey: 'sidebar.item.media', icon: Image, path: APP_ROUTES.MEDIA, relatedPaths: [APP_ROUTES.MEDIA], permission: dbConstants.PERMISSION_MEDIA_READ },
        ],
      },
      {
        id: 'shared',
        labelKey: 'sidebar.subgroup.shared',
        icon: Globe,
        items: [
          { id: 'persona', labelKey: 'sidebar.item.persona', icon: Users, path: APP_ROUTES.PERSONAS, relatedPaths: [APP_ROUTES.PERSONAS, constants.ROUTE_PERSONA_NEW], permission: dbConstants.PERMISSION_PERSONA_READ },
          { id: 'writing-style', labelKey: 'sidebar.item.writingStyle', icon: Type, path: APP_ROUTES.WRITING_STYLES, relatedPaths: [APP_ROUTES.WRITING_STYLES, constants.ROUTE_WRITING_STYLE_NEW], permission: dbConstants.PERMISSION_WRITING_STYLE_READ },
        ],
      },
    ],
  },
  {
    id: 'plan',
    labelKey: 'sidebar.group.plan',
    collapsible: true,
    items: [
      { id: 'campaign', labelKey: 'sidebar.item.campaign', icon: Megaphone, path: APP_ROUTES.CAMPAIGNS, relatedPaths: [APP_ROUTES.CAMPAIGNS, constants.ROUTE_CAMPAIGN_NEW], permission: dbConstants.PERMISSION_CAMPAIGN_READ },
    ],
  },
  {
    id: 'create',
    labelKey: 'sidebar.group.create',
    collapsible: true,
    items: [
      { id: 'content', labelKey: 'sidebar.item.content', icon: FileCheck, path: APP_ROUTES.CONTENTS, relatedPaths: [APP_ROUTES.CONTENTS, constants.ROUTE_CONTENT_NEW], permission: dbConstants.PERMISSION_CONTENT_READ },
    ],
  },
  {
    id: 'manage',
    labelKey: 'sidebar.group.manage',
    collapsible: true,
    items: [
      { id: 'channel', labelKey: 'sidebar.item.channel', icon: Radio, path: APP_ROUTES.CHANNELS, relatedPaths: [APP_ROUTES.CHANNELS, constants.ROUTE_CHANNELS], permission: dbConstants.PERMISSION_PUBLISH_READ },
      { id: 'editorial-calendar', labelKey: 'sidebar.item.editorialCalendar', icon: CalendarDays, path: APP_ROUTES.EDITORIAL_CALENDAR, relatedPaths: [APP_ROUTES.EDITORIAL_CALENDAR], permission: dbConstants.PERMISSION_PUBLISH_READ },
    ],
  },
];

export const isSelectedPath = (pathname: string, paths: string[]) => {
  for (const path of paths) {
    if (pathname === path) return true;
    if (path !== constants.ROUTE_HOME && pathname.startsWith(path)) return true;
  }
  return false;
};

export const filterMenuGroups = (
  groups: MenuGroup[],
  canView: (item: MenuItem) => boolean,
): MenuGroup[] =>
  groups
    .map((group) => ({
      ...group,
      items: group.items.filter(canView),
      subgroups: (group.subgroups ?? [])
        .map((subgroup) => ({ ...subgroup, items: subgroup.items.filter(canView) }))
        .filter((subgroup) => subgroup.items.length > 0),
    }))
    .filter((group) => group.items.length > 0 || group.subgroups.length > 0);
